import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
	EuiButton,
	EuiButtonIcon,
	EuiFlexGroup,
	EuiFlexItem,
	EuiHorizontalRule,
	EuiLoadingSpinner,
	EuiPageBody,
	EuiPageHeader,
	EuiPageSection,
	EuiPageTemplate,
	EuiText,
	EuiToolTip,
} from '@equipmentshare/ds2'
import { CloseLineIcon } from '@equipmentshare/ds2-icons'
import { heapEvent } from '@equipmentshare/heaper'
import { InventoryLocation } from 'inventory-web'
import {
	CreateDemandRequestInput,
	DemandRequestTargetType,
	DemandRequestType,
	useCreateDemandRequestMutation,
	useInventoryLocationsQuery
} from '../../graphql'
import { useShowError, useShowSuccess } from 'hooks/useToast'
import HeaderForm from './components/HeaderForm'
import LineItemsList from './components/LineItemsList'
import { useDemandRequestForm, CONTACT_PHONE_STORAGE_KEY, setStoredAlternateAddress } from './hooks/useDemandRequestForm'
import useDistributionCenterOptions from './hooks/useDistributionCenterOptions'
import { MAIN_DISTRIBUTION_CENTER_ID } from './RequestDetails.constants'
import muiTheme from '../../config/muiTheme'
import { LAYOUT_CONSTANTS, Z_INDEX } from './constants/layoutConstants'
import { LocationState } from './types'

export default function NewRequest() {
	const navigate = useNavigate()
	const location = useLocation()
	const locationState = location.state as LocationState | undefined

	// Form state management
	const form = useDemandRequestForm()

	// GraphQL mutation hook
	const [ createDemandRequest, { loading: mutationLoading, error: mutationError } ] = useCreateDemandRequestMutation()

	// The DCs this requesting location may be fulfilled from
	const distributionCenters = useDistributionCenterOptions( form.formData.requestingInventoryId )

	// Get saved requesting inventory ID from local storage
	const savedRequestingInventoryId = localStorage.getItem( 'demandRequestRequestingInventoryId' )

	// Fetch the saved requesting inventory if available
	const { data: savedRequestingInventoryData } = useInventoryLocationsQuery( {
		variables: {
			inventoryLocationIds: savedRequestingInventoryId ? [ savedRequestingInventoryId ] : [],
		},
		skip: !savedRequestingInventoryId,
	} )

	// Toast hooks for success and error notifications
	const showSuccess = useShowSuccess()
	const showError = useShowError()

	// State for loading and errors
	const [ isLoading, setIsLoading ] = useState( false )
	const [ hasError, setHasError ] = useState( false )
	const [ errorMessage, setErrorMessage ] = useState( '' )
	const [ showValidationErrors, setShowValidationErrors ] = useState( false )
	const [ isErrorPanelDismissed, setIsErrorPanelDismissed ] = useState( false )

	// State for selected inventory objects (for display)
	const [ selectedRequestingInventory, setSelectedRequestingInventory ] = useState<InventoryLocation>()

	// The DC the request goes to ('' = Automatic). A location no mini-DC serves is
	// always fulfilled by the main DC. A served one keeps the picked DC while it is
	// still offered for the current requesting location, else falls back to
	// Automatic — derived here so a change of requesting location needs no reset.
	const isPickedDcOffered = distributionCenters.options.some( ( dc ) => dc.id === form.formData.fulfillingInventoryId )
	const fulfillingInventoryId = distributionCenters.servedByMiniDc
		? ( isPickedDcOffered ? form.formData.fulfillingInventoryId : '' )
		: MAIN_DISTRIBUTION_CENTER_ID

	// Restore requesting inventory from local storage on mount
	useEffect( () => {
		const edge = savedRequestingInventoryData?.inventoryLocations?.edges?.[ 0 ]
		if ( edge?.node ) {
			const savedInventory = edge.node
			if ( !form.formData.requestingInventoryId ) {
				form.setRequestingInventoryId( savedInventory.id.toString() )
				setSelectedRequestingInventory( savedInventory )
			}
		}
	}, [ savedRequestingInventoryData ] ) // eslint-disable-line react-hooks/exhaustive-deps

	// Handle GraphQL mutation errors by showing toast notifications
	useEffect( () => {
		if ( mutationError ) {
			showError( `demandRequestMutationError${Date.now()}`, mutationError.message || 'A GraphQL error occurred' )
		}
	}, [ mutationError, showError ] )

	// Map form data to GraphQL mutation input format
	const mapFormDataToMutationInput = (): CreateDemandRequestInput => {
		return {
			requestingInventoryId: form.formData.requestingInventoryId,
			// Automatic (blank) lets inventory-api route the request to a serving DC
			fulfillingInventoryId: fulfillingInventoryId || null,
			requestType: DemandRequestType.Stock,
			contactPhonePrimary: form.formData.contactPhone.trim() || null,
			ccEmail: form.formData.ccEmail.trim() || null,
			notes: form.formData.notes?.trim() || null,
			// Null tells the API to deliver to the branch's Fleet address.
			deliveryAddress: form.formData.deliverToAlternateAddress
				? {
					line1: form.formData.alternateAddress.line1.trim(),
					line2: form.formData.alternateAddress.line2.trim() || null,
					city: form.formData.alternateAddress.city.trim(),
					state: form.formData.alternateAddress.state.trim(),
					postalCode: form.formData.alternateAddress.postalCode.trim(),
				}
				: null,
			lineItems: form.formData.lineItems
				.filter( ( lineItem ) => lineItem.productId && lineItem.quantity ) // Only include line items with product and quantity
				.map( ( lineItem ) => ( {
					productId: lineItem.productId,
					quantityRequested: lineItem.quantity,
					targetType: lineItem.targetType ? lineItem.targetType as DemandRequestTargetType : null,
					targetId: lineItem.targetId?.trim() || null,
					allowSubstitution: lineItem.allowSubstitution,
					willCall: lineItem.willCall,
					// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
					lineNotes: lineItem.notes?.trim() || null,
					// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
					neededBy: lineItem.neededByDate?.trim() || null,
					// selectedPart is excluded - it's only for UI display
				} ) )
		}
	}

	const handleCancel = () => {
		heapEvent( 'Inventory - Stock Request - Page - Click - Cancel', {} )
		form.resetForm()
		setShowValidationErrors( false )
		navigate( locationState?.from ?? '/inventory' )
	}

	// Inventory selection handlers
	const handleRequestingInventoryChange = ( id: number, inventory?: InventoryLocation ) => {
		const idString = id ? id.toString() : ''
		form.setRequestingInventoryId( idString )
		setSelectedRequestingInventory( inventory )

		// Save to local storage
		if ( idString ) {
			localStorage.setItem( 'demandRequestRequestingInventoryId', idString )
		} else {
			localStorage.removeItem( 'demandRequestRequestingInventoryId' )
		}
	}

	const handleFulfillingInventoryChange = ( id: string ) => {
		form.setFulfillingInventoryId( id )
	}

	const handleSubmit = async() => {
		heapEvent( 'Inventory - Stock Request - Page - Click - Submit', {} )

		// Show validation errors from this point forward
		setShowValidationErrors( true )
		// Reset error panel dismissed state when validation runs
		setIsErrorPanelDismissed( false )

		// Validate form before submission
		if ( !form.validateForm() ) {
			setHasError( true )
			setErrorMessage( 'Please correct the errors in the form before submitting.' )
			return
		}

		setIsLoading( true )
		setHasError( false )
		setErrorMessage( '' )

		try {
			// Map form data to mutation input and execute GraphQL mutation
			const input = mapFormDataToMutationInput()
			const result = await createDemandRequest( {
				variables: { input }
			} )

			const response = result.data?.createDemandRequest

			if ( response?.success ) {
				// Success - show toast and navigate back to previous view
				const successMessage = response.message || 'Request has been sent'
				showSuccess( `demandRequestSuccess${Date.now()}`, successMessage )
				heapEvent( 'Inventory - Stock Request - Page - Submit - Success', {} )

				localStorage.setItem( `demandRequest/ccEmail/branch/${form.formData.requestingInventoryId}`, form.formData.ccEmail )
				localStorage.setItem( CONTACT_PHONE_STORAGE_KEY, form.formData.contactPhone )
				setStoredAlternateAddress(
					form.formData.requestingInventoryId,
					form.formData.deliverToAlternateAddress,
					form.formData.alternateAddress,
				)

				form.resetForm()
				setShowValidationErrors( false )
				navigate( locationState?.from ?? '/' )
			} else {
				// Handle API error response
				throw new Error( response?.message ?? 'Demand request creation failed' )
			}
		} catch ( error ) {
			setHasError( true )
			const errorMsg = error instanceof Error ? error.message : 'An error occurred while creating the demand request'
			setErrorMessage( errorMsg )

			// Show error toast notification
			showError( `demandRequestError${Date.now()}`, errorMsg )
			heapEvent( 'Inventory - Stock Request - Page - Submit - Error', {
				error: errorMsg,
			} )
		} finally {
			setIsLoading( false )
		}
	}

	const renderSubmitButton = () => {
		// Also held while the DC options load: until then the request's DC is not settled
		const submitButton = (
			<EuiButton
				fill
				onClick={handleSubmit}
				isLoading={isLoading || mutationLoading}
				disabled={!form.isValid || isLoading || mutationLoading || distributionCenters.loading}
			>
				Send Request for {form.totalQuantity} {form.totalQuantity === 1 ? 'Item' : 'Items'}
			</EuiButton>
		)

		// Only explain the disabled state when it's caused by unmet requirements,
		// not while a request is in flight.
		if ( form.isValid || isLoading || mutationLoading ) {
			return submitButton
		}

		const tooltipContent = form.missingRequirements.length > 0 ? (
			<ul style={{ margin: 0, paddingInlineStart: '1.25em' }}>
				{form.missingRequirements.map( ( requirement ) => (
					<li key={requirement}>{requirement}</li>
				) )}
			</ul>
		) : (
			'Complete all required fields before submitting.'
		)

		return (
			<EuiToolTip position="top" content={tooltipContent}>
				{submitButton}
			</EuiToolTip>
		)
	}

	return (
		<div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
			<EuiPageBody paddingSize="none" style={{ flex: 1, paddingBottom: `${LAYOUT_CONSTANTS.contentBottomPadding}px`, overflowY: 'auto' }}>
				<EuiPageTemplate paddingSize="none" style={{ paddingBlockStart: 0 }}>
					{/* Page Header with divider */}
					<EuiPageHeader
						pageTitle={
							<EuiFlexGroup alignItems="center" gutterSize="l">
								<EuiFlexItem grow={false}>
									<EuiText>
										<h2 style={{ margin: 0, fontSize: '1.25rem' }}>Stock Request</h2>
									</EuiText>
								</EuiFlexItem>
							</EuiFlexGroup>
						}
						paddingSize="m"
						bottomBorder="extended"
					/>

					<EuiPageSection paddingSize="none">
						{/* Loading State */}
						{( isLoading || mutationLoading ) && (
							<div style={{ padding: `${LAYOUT_CONSTANTS.contentPadding}px` }}>
								<EuiFlexGroup justifyContent="spaceAround">
									<EuiFlexItem grow={false}>
										<EuiLoadingSpinner size="l" />
									</EuiFlexItem>
								</EuiFlexGroup>
							</div>
						)}

						{/* Form Content with padding */}
						<div style={{ padding: `${LAYOUT_CONSTANTS.contentPadding}px` }}>
							<HeaderForm
								requestingInventoryId={form.formData.requestingInventoryId}
								contactPhone={form.formData.contactPhone}
								ccEmail={form.formData.ccEmail}
								notes={form.formData.notes}
								deliverToAlternateAddress={form.formData.deliverToAlternateAddress}
								alternateAddress={form.formData.alternateAddress}
								onRequestingInventoryChange={handleRequestingInventoryChange}
								onFulfillingInventoryChange={handleFulfillingInventoryChange}
								onContactPhoneChange={form.setContactPhone}
								onContactPhoneBlur={form.validateContactPhoneOnBlur}
								onCCEmailChange={form.setCCEmail}
								onCCEmailBlur={form.validateCCEmailOnBlur}
								onNotesChange={form.setNotes}
								onDeliverToAlternateAddressChange={form.setDeliverToAlternateAddress}
								onAlternateAddressFieldChange={form.setAlternateAddressField}
								validation={form.validation}
								showValidationErrors={showValidationErrors}
								selectedRequestingInventory={selectedRequestingInventory}
								fulfillingInventoryId={fulfillingInventoryId}
								distributionCenters={distributionCenters}
							/>
						</div>

						{/* Divider between sections - full width, no padding */}
						<EuiHorizontalRule margin="none" />

						{/* Line Items with padding */}
						<div style={{ padding: `${LAYOUT_CONSTANTS.contentPadding}px` }}>
							<LineItemsList
								lineItems={form.lineItems}
								onAddLineItem={form.addLineItem}
								onRemoveLineItem={form.removeLineItem}
								onUpdateLineItem={form.updateLineItem}
								hasValidationErrors={form.validation.lineItems.isInvalid}
								validationErrorMessage={form.validation.lineItems.error}
								showValidationErrors={showValidationErrors}
								branchId={selectedRequestingInventory?.branchId}
							/>
						</div>

						{/* Add bottom padding to prevent content from being hidden behind sticky footer */}
						<div style={{ paddingBottom: `${LAYOUT_CONSTANTS.contentBottomPadding}px` }} />
					</EuiPageSection>
				</EuiPageTemplate>
			</EuiPageBody>

			{/* Sticky Error Panel - appears above footer when there are errors */}
			{( hasError || mutationError ) && !isErrorPanelDismissed && (
				<div
					style={{
						position: 'fixed',
						bottom: `${LAYOUT_CONSTANTS.footerHeight}px`,
						left: 0,
						right: 0,
						backgroundColor: muiTheme.palette.formPanel.errorBackground,
						border: `2px solid ${muiTheme.palette.formPanel.errorText}`,
						padding: `${LAYOUT_CONSTANTS.errorPanelPaddingVertical}px ${LAYOUT_CONSTANTS.errorPanelPaddingHorizontal}px`,
						boxShadow: muiTheme.palette.formPanel.shadow,
						zIndex: Z_INDEX.stickyErrorPanel,
					}}
				>
					<EuiFlexGroup gutterSize="m" justifyContent="spaceBetween" alignItems="center">
						<EuiFlexItem>
							<EuiText style={{ color: muiTheme.palette.formPanel.errorText }}>
								<strong>Error:</strong> {errorMessage ?? mutationError?.message ?? 'An error occurred'}
							</EuiText>
						</EuiFlexItem>
						<EuiFlexItem grow={false}>
							<EuiButtonIcon
								iconType={CloseLineIcon}
								color="danger"
								aria-label="Close error message"
								onClick={() => setIsErrorPanelDismissed( true )}
								display="base"
							/>
						</EuiFlexItem>
					</EuiFlexGroup>
				</div>
			)}

			{/* Sticky Footer */}
			<div
				style={{
					position: 'fixed',
					bottom: 0,
					left: 0,
					right: 0,
					backgroundColor: muiTheme.palette.formPanel.footerBackground,
					padding: `${LAYOUT_CONSTANTS.footerPaddingVertical}px ${LAYOUT_CONSTANTS.footerPaddingHorizontal}px`,
					boxShadow: muiTheme.palette.formPanel.shadow,
					zIndex: Z_INDEX.stickyFooter,
				}}
			>
				<EuiFlexGroup gutterSize="m" justifyContent="flexEnd">
					<EuiFlexItem grow={false} style={{ minWidth: LAYOUT_CONSTANTS.minCancelButtonWidth }}>
						<EuiButton
							color="text"
							style={{
								backgroundColor: 'transparent',
								border: `1px solid ${muiTheme.palette.common.white}`,
								color: muiTheme.palette.common.white,
							}}
							onClick={handleCancel}
							disabled={isLoading || mutationLoading}
						>
							Cancel
						</EuiButton>
					</EuiFlexItem>
					<EuiFlexItem grow={false} style={{ minWidth: LAYOUT_CONSTANTS.minSubmitButtonWidth }}>
						{renderSubmitButton()}
					</EuiFlexItem>
				</EuiFlexGroup>
			</div>
		</div>
	)
}
